import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Mail, RefreshCw, Send, CheckCircle2, XCircle, Eye, X, Paperclip, ShieldCheck, ShieldAlert, AlertTriangle } from "lucide-react";
import DOMPurify from "dompurify";

/**
 * Email delivery log panel — every send attempt (via ``send_email_auto``)
 * lands here so admins can see when a partner statement or password-reset
 * actually left the building. Failed rows can be re-fired straight from
 * the row action button. Preview modal shows the exact HTML body Microsoft
 * (or SMTP) received.
 *
 * Attachments are NOT stored on log rows — that would bloat Mongo.
 * The row records ``has_attachments`` + filename summary; a re-send
 * from the log ships the message body only (with a warning banner).
 */
function fmtTs(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-AU", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return iso; }
}

function StatusPill({ status, error }) {
  const map = {
    sent:    { icon: CheckCircle2,  cls: "bg-emerald-500/15 border-emerald-500/50 text-emerald-300", label: "Sent" },
    failed:  { icon: XCircle,       cls: "bg-red-500/15 border-red-500/50 text-red-300",             label: "Failed" },
    skipped: { icon: AlertTriangle, cls: "bg-amber-500/15 border-amber-500/50 text-amber-300",       label: "Skipped" },
    pending: { icon: RefreshCw,     cls: "bg-slate-800 border-slate-700 text-slate-300",             label: "Pending" },
  };
  const cfg = map[status] || map.pending;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-widest ${cfg.cls}`}
      title={error || cfg.label}
    >
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

// Human-friendly copy for each skip_reason the backend writes. Keep in
// sync with commissions.routes_payouts._log_skipped_payout_send call
// sites — adding a new reason there means adding a clue here.
const SKIP_REASON_LABEL = {
  toggle_disabled:   "Auto-email toggle off",
  demo_mode:         "Demo mode on",
  no_email_on_file:  "No partner email",
};

function SkipReasonBadge({ reason }) {
  if (!reason) return null;
  const label = SKIP_REASON_LABEL[reason] || reason;
  return (
    <span
      className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded-full border text-[10px] font-mono uppercase tracking-widest bg-amber-500/10 border-amber-500/40 text-amber-200"
      title={`skip_reason: ${reason}`}
    >
      {label}
    </span>
  );
}

function ProviderPill({ provider }) {
  const cls = provider === "graph"
    ? "bg-cyan-500/15 border-cyan-500/50 text-cyan-300"
    : provider === "smtp"
      ? "bg-slate-800 border-slate-700 text-slate-300"
      : "bg-red-500/15 border-red-500/50 text-red-300";
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] font-mono uppercase tracking-widest ${cls}`}>
      {provider === "graph" ? "M365" : provider === "smtp" ? "SMTP" : (provider || "—")}
    </span>
  );
}

export default function EmailDeliveryLog() {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  // Filters — client-side against the last 50 rows so switching is instant.
  const [statusFilter, setStatusFilter] = useState("all");   // all | sent | failed | skipped
  const [kindFilter, setKindFilter] = useState("all");       // all | <kind>
  const [skipReasonFilter, setSkipReasonFilter] = useState("all"); // all | toggle_disabled | ...

  async function reload() {
    setBusy(true);
    try {
      const { data } = await api.get("/commissions/email-log?limit=50");
      setRows(data.rows || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load email log");
    } finally { setBusy(false); }
  }
  useEffect(() => { reload(); }, []);

  // Derive dropdown options + filtered view. Options come from the
  // actual data so we never list a kind/reason that isn't present.
  const kindOptions = useMemo(() => {
    const set = new Set((rows || []).map(r => r.kind).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);
  const skipReasonOptions = useMemo(() => {
    const set = new Set((rows || [])
      .filter(r => r.status === "skipped")
      .map(r => r.skip_reason).filter(Boolean));
    return Array.from(set).sort();
  }, [rows]);
  const skippedCount = useMemo(
    () => (rows || []).filter(r => r.status === "skipped").length,
    [rows]);
  const filtered = useMemo(() => {
    if (!rows) return null;
    return rows.filter(r => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (kindFilter !== "all" && (r.kind || "") !== kindFilter) return false;
      if (skipReasonFilter !== "all") {
        if (r.status !== "skipped") return false;
        if ((r.skip_reason || "") !== skipReasonFilter) return false;
      }
      return true;
    });
  }, [rows, statusFilter, kindFilter, skipReasonFilter]);

  function clearFilters() {
    setStatusFilter("all");
    setKindFilter("all");
    setSkipReasonFilter("all");
  }
  const filtersActive = statusFilter !== "all" || kindFilter !== "all" || skipReasonFilter !== "all";

  async function resend(row) {
    if (!window.confirm(
      row.has_attachments
        ? `Resend to ${row.to}?\n\nWarning: the original message had attachments (${(row.attachment_summary || []).join(", ")}) — those cannot be reattached from the log. Only the message body will be re-sent.`
        : `Resend to ${row.to}?`
    )) return;
    try {
      await api.post(`/commissions/email-log/${row.id}/resend`, {});
      toast.success(`Resent to ${row.to}`);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Resend failed");
    }
  }

  async function checkStatus(row) {
    if (row.provider !== "graph") {
      toast.error("Delivery check only works for Microsoft Graph sends");
      return;
    }
    try {
      const { data } = await api.post(`/commissions/email-log/${row.id}/check-status`);
      const dc = data.delivery_check || {};
      if (dc.status === "bounced") {
        toast.error(`Bounced — ${dc.reason?.slice(0, 120) || "reason unknown"}`);
      } else {
        toast.success("No bounce detected — Microsoft delivered it and no NDR came back");
      }
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Status check failed");
    }
  }

  async function openPreview(row) {
    try {
      const { data } = await api.get(`/commissions/email-log/${row.id}`);
      setPreview(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Preview failed");
    }
  }

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-5" data-testid="email-delivery-log">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h2 className="text-slate-100 text-lg font-semibold inline-flex items-center gap-2">
            <Mail className="w-4 h-4 text-cyan-300" /> Email delivery log
          </h2>
          <p className="text-xs text-slate-400 mt-1 max-w-2xl">
            Every email the system attempts — password resets, payout statements, partner activity notes, signup confirmations. Rows auto-prune after 90 days. Click <b>Resend</b> if Microsoft accepted it but the partner never received it, or to retry a failed send. <b>Skipped</b> rows show sends that were intentionally not dispatched (auto-email toggle off, demo mode, missing partner email) — the reason badge tells you which.
          </p>
        </div>
        <button
          type="button"
          onClick={reload}
          disabled={busy}
          data-testid="email-log-refresh"
          className="inline-flex items-center gap-1 text-xs text-slate-400 hover:text-cyan-300 px-2 py-1 rounded border border-slate-800 hover:border-slate-700"
        >
          <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      {/* Filter bar — client-side on the last 50 rows so switching is instant. */}
      <div className="flex flex-wrap items-center gap-2 mb-3 text-xs" data-testid="email-log-filters">
        <label className="inline-flex items-center gap-1.5">
          <span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">Status</span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="email-log-status-filter"
            className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-200 focus:outline-none focus:border-cyan-500"
          >
            <option value="all">All</option>
            <option value="sent">Sent</option>
            <option value="failed">Failed</option>
            <option value="skipped">Skipped</option>
            <option value="pending">Pending</option>
          </select>
        </label>
        {kindOptions.length > 0 && (
          <label className="inline-flex items-center gap-1.5">
            <span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">Kind</span>
            <select
              value={kindFilter}
              onChange={(e) => setKindFilter(e.target.value)}
              data-testid="email-log-kind-filter"
              className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-200 font-mono focus:outline-none focus:border-cyan-500"
            >
              <option value="all">All</option>
              {kindOptions.map(k => <option key={k} value={k}>{k}</option>)}
            </select>
          </label>
        )}
        {skipReasonOptions.length > 0 && (
          <label className="inline-flex items-center gap-1.5">
            <span className="text-slate-500 uppercase tracking-widest text-[10px] font-semibold">Skip reason</span>
            <select
              value={skipReasonFilter}
              onChange={(e) => setSkipReasonFilter(e.target.value)}
              data-testid="email-log-skip-reason-filter"
              className="bg-slate-950 border border-slate-800 rounded px-2 py-1 text-slate-200 focus:outline-none focus:border-cyan-500"
            >
              <option value="all">All</option>
              {skipReasonOptions.map(r => (
                <option key={r} value={r}>{SKIP_REASON_LABEL[r] || r}</option>
              ))}
            </select>
          </label>
        )}
        <button
          type="button"
          onClick={() => setStatusFilter(statusFilter === "skipped" ? "all" : "skipped")}
          data-testid="email-log-show-skipped-only"
          className={`inline-flex items-center gap-1 px-2 py-1 rounded border font-semibold uppercase tracking-widest text-[10px] ${
            statusFilter === "skipped"
              ? "bg-amber-500/20 border-amber-500/60 text-amber-200"
              : "bg-slate-950 border-slate-800 text-slate-400 hover:text-amber-300 hover:border-slate-700"
          }`}
          title="Toggle to show only sends that were intentionally not dispatched"
        >
          <AlertTriangle className="w-3 h-3" /> Show skipped only {skippedCount > 0 && <span className="font-mono">· {skippedCount}</span>}
        </button>
        {filtersActive && (
          <button
            type="button"
            onClick={clearFilters}
            data-testid="email-log-clear-filters"
            className="inline-flex items-center gap-1 px-2 py-1 rounded border border-slate-800 text-slate-400 hover:text-slate-100"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <div className="ml-auto text-[11px] text-slate-500 font-mono">
          {rows === null ? "" : `${(filtered || []).length} / ${rows.length}`}
        </div>
      </div>

      <div className="overflow-x-auto -mx-5 px-5">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            <tr className="border-b border-slate-800">
              <th className="text-left py-2 pr-3">When</th>
              <th className="text-left py-2 pr-3">Via</th>
              <th className="text-left py-2 pr-3">To</th>
              <th className="text-left py-2 pr-3">Subject</th>
              <th className="text-left py-2 pr-3">Status</th>
              <th className="text-right py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered === null ? (
              <tr><td colSpan={6} className="py-10 text-center text-slate-500">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="py-10 text-center text-slate-500">
                {rows && rows.length > 0
                  ? "No rows match the current filters."
                  : "No emails sent yet. This fills up as the system dispatches password resets, payout statements, and partner notifications."}
              </td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} className="border-b border-slate-800/60 hover:bg-slate-950/40" data-testid={`email-log-row-${r.id}`}>
                <td className="py-2 pr-3 font-mono text-[11px] text-slate-400 whitespace-nowrap">{fmtTs(r.created_at)}</td>
                <td className="py-2 pr-3"><ProviderPill provider={r.provider} /></td>
                <td className="py-2 pr-3 text-slate-200 truncate max-w-[220px]" title={r.to}>{r.to}</td>
                <td className="py-2 pr-3 text-slate-300 truncate max-w-[280px]" title={r.subject}>
                  {r.subject}
                  {r.has_attachments && (
                    <Paperclip className="w-3 h-3 inline ml-1.5 text-slate-500" aria-label="Has attachments" />
                  )}
                  {r.kind && (
                    <span className="ml-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono">{r.kind}</span>
                  )}
                </td>
                <td className="py-2 pr-3"><StatusPill status={r.status} error={r.error} />
                  {r.status === "skipped" && <SkipReasonBadge reason={r.skip_reason} />}
                  {r.delivery_check?.status === "bounced" && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-widest bg-red-500/15 border-red-500/50 text-red-300"
                      title={r.delivery_check?.reason || ""}
                    >
                      <ShieldAlert className="w-3 h-3" /> Bounced
                    </span>
                  )}
                  {r.delivery_check?.status === "delivered_probably" && (
                    <span
                      className="ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[10px] font-semibold uppercase tracking-widest bg-emerald-500/10 border-emerald-500/40 text-emerald-300"
                      title={`Checked ${r.delivery_check.checked_at} — no NDR in sender inbox`}
                    >
                      <ShieldCheck className="w-3 h-3" /> No bounce
                    </span>
                  )}
                </td>
                <td className="py-2 text-right">
                  <button
                    type="button"
                    onClick={() => openPreview(r)}
                    data-testid={`email-log-preview-${r.id}`}
                    title="Preview the HTML body sent"
                    className="text-slate-400 hover:text-cyan-300 p-1"
                  >
                    <Eye className="w-3.5 h-3.5" />
                  </button>
                  {r.provider === "graph" && r.status === "sent" && (
                    <button
                      type="button"
                      onClick={() => checkStatus(r)}
                      data-testid={`email-log-check-${r.id}`}
                      title="Scan sender mailbox for a bounce (Mail.Read needed)"
                      className="text-slate-400 hover:text-amber-300 p-1 ml-1"
                    >
                      <ShieldCheck className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => resend(r)}
                    data-testid={`email-log-resend-${r.id}`}
                    title={`Re-send this email to ${r.to}`}
                    className="text-slate-400 hover:text-emerald-300 p-1 ml-1"
                  >
                    <Send className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {preview && (
        <PreviewModal entry={preview} onClose={() => setPreview(null)} onResend={() => { resend(preview); setPreview(null); }} />
      )}
    </section>
  );
}

function PreviewModal({ entry, onClose, onResend }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex justify-end" onClick={onClose}>
      <div
        className="w-full max-w-2xl h-full bg-slate-900 border-l border-slate-800 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="email-log-preview-modal"
      >
        <div className="p-6 border-b border-slate-800 flex items-start justify-between gap-3 sticky top-0 bg-slate-900 z-10">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-cyan-400 font-semibold flex items-center gap-2">
              Email preview <ProviderPill provider={entry.provider} /> <StatusPill status={entry.status} error={entry.error} />
            </div>
            <h2 className="font-display text-lg tracking-tight mt-1 truncate">{entry.subject}</h2>
            <div className="text-xs text-slate-500 mt-1">
              <span className="text-slate-300">{entry.to}</span>
              {(entry.bcc || []).length > 0 && <span> · bcc: {entry.bcc.join(", ")}</span>}
              <span className="ml-2 font-mono">{fmtTs(entry.created_at)}</span>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-100" data-testid="email-log-preview-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {entry.error && (
            <div className="rounded-lg border border-red-500/50 bg-red-500/10 p-3 text-xs text-red-200">
              <div className="font-semibold uppercase tracking-widest text-[10px] mb-1">Error</div>
              <div className="font-mono break-all">{entry.error}</div>
            </div>
          )}
          {entry.has_attachments && (
            <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
              <div className="font-semibold uppercase tracking-widest text-[10px] mb-1 inline-flex items-center gap-1">
                <Paperclip className="w-3 h-3" /> Attachments (not stored)
              </div>
              <div>{(entry.attachment_summary || []).join(", ") || "unknown"}</div>
              <div className="mt-1 text-amber-300/80">Resending from the log will send the message body only — the attachments won&apos;t be reattached. For payout statements, use the payout list to regenerate.</div>
            </div>
          )}
          <div className="rounded-lg border border-slate-800 bg-slate-950 p-4 overflow-x-auto"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(entry.html || "<p>(no body)</p>") }} />
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onResend}
              data-testid="email-log-preview-resend"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-semibold"
            >
              <Send className="w-3.5 h-3.5" /> Resend to {entry.to}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
